import { useEffect, useRef, useState } from "react"
import {
  CandidateTestUnlockRequest_TableFragment,
  CandidateTestUnlockRequestSortEnum,
  CandidateTestUnlockRequestStateEnum,
  SortDirectionEnum,
} from "~/__generated__/graphql"
import { Button } from "~/shadcn/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/shadcn/ui/dropdown-menu"
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHead,
  TableRow,
} from "~/ui/tables/Table"
import { useMemo } from "react"
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react"
import EllipsisVerticalIcon from "~/images/icons/ellipsis_vertical.svg?react"
import { cn } from "~/common/shadcn-utils"
import { gql } from "~/__generated__"
import { useSafeMutation } from "~/common/useSafeMutation"
import toast from "react-hot-toast"
import { CandidateTestUnlockRequestSort } from "./CandidateTestUnlockRequests"
import { GravatarImage } from "~/ui/GravatarImage"
import { formatDateAndTime } from "~/common/dates"

const TruncatedNote = ({ text }: { text: string }) => {
  const [expanded, setExpanded] = useState(false)
  const textRef = useRef<HTMLDivElement>(null)
  const [needsTruncation, setNeedsTruncation] = useState(false)

  useEffect(() => {
    const checkHeight = () => {
      if (textRef.current) {
        const isOverflowing =
          textRef.current.scrollHeight > textRef.current.clientHeight + 5
        setNeedsTruncation(isOverflowing)
      }
    }

    checkHeight()
    window.addEventListener("resize", checkHeight)
    return () => window.removeEventListener("resize", checkHeight)
  }, [text])

  return (
    <div className="flex flex-col items-start justify-center">
      <div ref={textRef} className={`${expanded ? "" : "line-clamp-3"}`}>
        {text}
      </div>
      {needsTruncation && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="text-blue-500 hover:text-blue-700 text-sm font-medium mt-1"
        >
          Read more
        </button>
      )}
      {expanded && (
        <button
          onClick={() => setExpanded(false)}
          className="text-blue-500 hover:text-blue-700 text-sm font-medium mt-1"
        >
          Show less
        </button>
      )}
    </div>
  )
}

type CandidateTestUnlockRequestColumns =
  | "candidate"
  | "testName"
  | "reason"
  | "createdAt"
  | "actions"

export interface CandidateTestUnlockRequestsTableProps {
  data: CandidateTestUnlockRequest_TableFragment[]
  columns?: CandidateTestUnlockRequestColumns[]
  viewPath: (
    candidateTestUnlockRequest: CandidateTestUnlockRequest_TableFragment
  ) => string
  sort?: CandidateTestUnlockRequestSort
  setSort?: (sort: CandidateTestUnlockRequestSort) => void
  onCandidateTestUnlockRequestAction: (requestId: string, newState: any) => void
}

export const CandidateTestUnlockRequestsTable = ({
  data,
  columns = ["candidate", "testName", "reason", "createdAt", "actions"],
  viewPath,
  sort,
  setSort,
  onCandidateTestUnlockRequestAction,
}: CandidateTestUnlockRequestsTableProps) => {
  const [approveRequest] = useSafeMutation(
    CANDIDATE_TEST_UNLOCK_REQUEST_APPROVE_MUTATION
  )
  const [denyRequest] = useSafeMutation(
    CANDIDATE_TEST_UNLOCK_REQUEST_DENY_MUTATION
  )

  const onApproveRequest = async (id: string) => {
    const res = await approveRequest({
      variables: { input: { id } },
    })

    if (res.errors) {
      console.error(res.errors)
      toast.error("Error approving request.")
    } else {
      toast.success("Request approved.")
      if (
        res.data?.candidateTestUnlockRequestApprove?.candidateTestUnlockRequest
      ) {
        onCandidateTestUnlockRequestAction(
          id,
          res.data?.candidateTestUnlockRequestApprove
            ?.candidateTestUnlockRequest
        )
      }
    }
  }

  const onDenyRequest = async (id: string) => {
    const res = await denyRequest({
      variables: { input: { id } },
    })

    if (res.errors) {
      console.error(res.errors)
      toast.error("Error denying request.")
    } else {
      toast.success("Request denied.")
      if (
        res.data?.candidateTestUnlockRequestDeny?.candidateTestUnlockRequest
      ) {
        onCandidateTestUnlockRequestAction(
          id,
          res.data?.candidateTestUnlockRequestDeny?.candidateTestUnlockRequest
        )
      }
    }
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((column, index) => (
            <CandidateTestsTableHead
              key={index}
              column={column}
              sort={sort}
              setSort={setSort}
            />
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((response) => (
          <TableRow key={response.id} className="relative">
            {columns.map((column, index) => (
              <TableCell
                key={index}
                className={cn({
                  "text-right": ["actions"].includes(column),
                })}
              >
                {column === "candidate" && (
                  <div className="flex items-center min-w-[175px] gap-2">
                    <GravatarImage
                      name={response.candidateTest.user.name}
                      email={response.candidateTest.user.email}
                    />
                    <div className="flex flex-col">
                      <div className="text-base flex items-center gap-2">
                        {response.candidateTest.user.name}
                      </div>
                      <div className="text-gray-999 hidden md:block">
                        {response.candidateTest.user.email}
                      </div>
                    </div>
                  </div>
                )}
                {column === "testName" && (
                  <div className="flex items-center min-w-[175px] gap-2">
                    {response.candidateTest.test.name}
                  </div>
                )}
                {column === "reason" && <TruncatedNote text={response.note!} />}
                {column === "createdAt" && (
                  <div className="flex items-center min-w-[100px] gap-2">
                    {formatDateAndTime(response.createdAt)}
                  </div>
                )}
                {column === "actions" && (
                  <div className="flex flex-row items-center gap-2 justify-end min-w-[100px]">
                    {response.state ===
                    CandidateTestUnlockRequestStateEnum.Pending ? (
                      <div className="flex items-center gap-2 justify-end">
                        <Button
                          size="sm"
                          onClick={() => onApproveRequest(response.id)}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onDenyRequest(response.id)}
                        >
                          Deny
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 justify-end">
                        {response.state ===
                        CandidateTestUnlockRequestStateEnum.Approved ? (
                          <span className="text-green-500">Approved</span>
                        ) : (
                          <span className="text-red-500">Denied</span>
                        )}

                        <DropdownMenu>
                          <DropdownMenuTrigger>
                            <EllipsisVerticalIcon className="w-4 h-4 hover:text-primary" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            {response.state ===
                              CandidateTestUnlockRequestStateEnum.Approved && (
                              <DropdownMenuItem
                                onClick={() => onDenyRequest(response.id)}
                              >
                                Deny Request
                              </DropdownMenuItem>
                            )}
                            {response.state ===
                              CandidateTestUnlockRequestStateEnum.Denied && (
                              <DropdownMenuItem
                                onClick={() => onApproveRequest(response.id)}
                              >
                                Approve Request
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                  </div>
                )}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

interface CandidateTestsTableHeadProps {
  className?: string
  column: CandidateTestUnlockRequestColumns
  sort?: CandidateTestUnlockRequestSort
  setSort?: (sort: CandidateTestUnlockRequestSort) => void
}

const CandidateTestsTableHead = ({
  className = "",
  column,
  sort,
  setSort,
}: CandidateTestsTableHeadProps) => {
  const label = useMemo(() => {
    switch (column) {
      case "candidate":
        return "Name & Email"
      case "testName":
        return "Test Name"
      case "reason":
        return "Reason"
      case "createdAt":
        return "Timestamp"
      case "actions":
        return "Actions"
    }
  }, [column])

  const Content = useMemo(() => {
    let Slot = ({ children }: { children: string }) => <>{children}</>

    if (setSort && ["testName", "reason", "createdAt"].includes(column)) {
      let isSortedColumn = false
      if (
        column === "testName" &&
        sort?.sort === CandidateTestUnlockRequestSortEnum.TestName
      ) {
        isSortedColumn = true
      } else if (
        column === "reason" &&
        sort?.sort === CandidateTestUnlockRequestSortEnum.Note
      ) {
        isSortedColumn = true
      } else if (
        column === "createdAt" &&
        sort?.sort === CandidateTestUnlockRequestSortEnum.CreatedAt
      ) {
        isSortedColumn = true
      }
      Slot = ({ children }: { children: string }) => (
        <Button
          variant="tableHeader"
          size="content"
          onClick={() => {
            if (column === "testName") {
              setSort(
                new CandidateTestUnlockRequestSort(
                  CandidateTestUnlockRequestSortEnum.TestName,
                  (isSortedColumn && sort?.reverseDirection()) ||
                    SortDirectionEnum.Descending
                )
              )
            } else if (column === "reason") {
              setSort(
                new CandidateTestUnlockRequestSort(
                  CandidateTestUnlockRequestSortEnum.Note,
                  (isSortedColumn && sort?.reverseDirection()) ||
                    SortDirectionEnum.Descending
                )
              )
            } else if (column === "createdAt") {
              setSort(
                new CandidateTestUnlockRequestSort(
                  CandidateTestUnlockRequestSortEnum.CreatedAt,
                  (isSortedColumn && sort?.reverseDirection()) ||
                    SortDirectionEnum.Descending
                )
              )
            }
          }}
        >
          {children}
          {isSortedColumn ? (
            sort?.direction === SortDirectionEnum.Descending ? (
              <ChevronDown className="ml-1 w-4 h-4" />
            ) : (
              <ChevronUp className="ml-1 w-4 h-4" />
            )
          ) : (
            <ChevronsUpDown className="ml-1 w-4 h-4" />
          )}
        </Button>
      )
    }

    return <Slot>{label}</Slot>
  }, [label, column, setSort, sort])

  return (
    <TableHead
      className={cn(className, {
        "text-right": ["actions"].includes(column),
      })}
    >
      {Content}
    </TableHead>
  )
}

const CANDIDATE_TEST_UNLOCK_REQUEST_APPROVE_MUTATION = gql(`
  mutation CandidateTestUnlockRequestApprove($input: CandidateTestUnlockRequestApproveInput!) {
    candidateTestUnlockRequestApprove(input: $input) {
      candidateTestUnlockRequest {
        id
        note
        state
        createdAt

        candidateTest {
          id
          test {
            id
            name
          }
          user {
            id
            name
            email
          }
        }
      }
    }
  }
`)

const CANDIDATE_TEST_UNLOCK_REQUEST_DENY_MUTATION = gql(`
  mutation CandidateTestUnlockRequestDeny($input: CandidateTestUnlockRequestDenyInput!) {
    candidateTestUnlockRequestDeny(input: $input) {
      candidateTestUnlockRequest {
        id
        note
        state
        createdAt

        candidateTest {
          id
          test {
            id
            name
          }
          user {
            id
            name
            email
          }
        }
      }
    }
  }
`)
